import { Component, DestroyRef, OnInit, computed, effect, inject, signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Route, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { filter } from 'rxjs';
import { TokenStorageService } from '../core/auth/token-storage.service';
import { CurrentContextService } from '../core/context/current-context.service';
import { NavigationItem } from '../core/context/current-context.model';
import { NavigationTreeService } from '../core/navigation/navigation-tree.service';
import { DemoModeService } from '../core/navigation/demo-mode.service';
import { LocalizationService } from '../core/localization/localization.service';
import { currentAppScope } from '../core/scope/app-scope';
import { ThemeService } from '../core/theme/theme.service';
import { WayfindingRecentsService } from '../core/wayfinding/wayfinding-recents.service';
import { WayfindingService } from '../core/wayfinding/wayfinding.service';
import { isWorkScreen, normalizeRoute } from '../core/wayfinding/wayfinding-navigator';
import { WayfindingPlay, WayfindingRecent } from '../core/wayfinding/wayfinding.models';
import { TabManagerService } from './tab-manager.service';
import { TabTitleService } from './tab-title.service';

@Component({
  standalone: true,
  selector: 'ain-shell',
  imports: [ButtonModule, RouterLink, RouterLinkActive, RouterOutlet, ToastModule],
  template: `
    <p-toast />
    <div class="shell" [class.rtl]="direction() === 'rtl'" (click)="hideOverlays()" (contextmenu)="hideOverlays()">
      <aside>
        <h1>{{ isOrganizationScope() ? t('shell.organization') : t('shell.appName') }}</h1>
        @if (isOrganizationScope()) {
          <p class="context">{{ t('shell.org') }}: {{ context()?.organizationUnitId ?? t('shell.notSelected') }}</p>
          <label class="switcher">
            {{ t('shell.organizationSwitcher') }}
            <select [value]="context()?.organizationUnitId ?? ''" [attr.aria-label]="t('shell.organizationSwitcher')">
              <option [value]="context()?.organizationUnitId ?? ''">{{ context()?.organizationUnitId ?? t('shell.noOrganization') }}</option>
            </select>
          </label>
        }
        <nav>
          @for (suite of navigationTree(); track suite.key) {
            <section class="nav-suite">
              <button type="button" class="nav-toggle suite" (click)="toggleNode(suite.key)">
                <span>{{ t(suite.labelKey) }}</span>
                <span>{{ isExpanded(suite.key) ? '−' : '+' }}</span>
              </button>
              @if (isExpanded(suite.key)) {
                @for (module of suite.modules; track module.key) {
                  <section class="nav-module">
                    <button type="button" class="nav-toggle module" (click)="toggleNode(moduleNodeKey(suite.key, module.key))">
                      <span>{{ t(module.labelKey) }}</span>
                      <span>{{ isExpanded(moduleNodeKey(suite.key, module.key)) ? '−' : '+' }}</span>
                    </button>
                    @if (isExpanded(moduleNodeKey(suite.key, module.key))) {
                      @for (capability of module.capabilities; track capability.key) {
                        <section class="nav-capability" [class.nav-capability--platform-admin]="isPlatformAdminCapability(capability.key)">
                          <button type="button" class="nav-toggle capability" [class.nav-toggle--platform-admin]="isPlatformAdminCapability(capability.key)" (click)="toggleNode(capabilityNodeKey(suite.key, module.key, capability.key))">
                            <span class="nav-toggle-label">
                              @if (isPlatformAdminCapability(capability.key)) {
                                <span class="nav-platform-icon" aria-hidden="true">⚙</span>
                              }
                              <span>{{ t(capability.labelKey) }}</span>
                            </span>
                            <span>{{ isExpanded(capabilityNodeKey(suite.key, module.key, capability.key)) ? '−' : '+' }}</span>
                          </button>
                          @if (isExpanded(capabilityNodeKey(suite.key, module.key, capability.key))) {
                            <div class="nav-leaves">
                              @for (leaf of capability.children; track leaf.key) {
                                <a [routerLink]="leaf.route" routerLinkActive="active-link" class="nav-leaf" [class.nav-leaf--platform-admin]="isPlatformAdminCapability(capability.key)">
                                  @if (isPlatformAdminCapability(capability.key)) {
                                    <span class="nav-platform-icon" aria-hidden="true">⚙</span>
                                  }
                                  <span>{{ t(leaf.labelKey) }}</span>
                                </a>
                              }
                            </div>
                          }
                        </section>
                      }
                      @if (module.directLeaves.length > 0) {
                        <div class="nav-leaves direct">
                          @for (leaf of module.directLeaves; track leaf.key) {
                            <a [routerLink]="leaf.route" routerLinkActive="active-link" class="nav-leaf">
                              <span>{{ t(leaf.labelKey) }}</span>
                            </a>
                          }
                        </div>
                      }
                    }
                  </section>
                }
              }
            </section>
          }
        </nav>
      </aside>
      <main>
        <header>
          <span>{{ t('shell.dashboard') }}</span>
          <form class="nav-search" (click)="$event.stopPropagation()" (submit)="submitHeaderSearch($event)">
            <input
              id="wayfinding-ask-header"
              type="search"
              [value]="headerQuery()"
              [attr.aria-label]="t('wayfinding.askPlaceholder')"
              [placeholder]="t('wayfinding.askPlaceholder')"
              [attr.aria-expanded]="paletteOpen()"
              aria-controls="wayfinding-palette"
              aria-autocomplete="list"
              (focus)="openPalette()"
              (input)="onHeaderQuery($event)" />
            @if (paletteOpen()) {
              <div id="wayfinding-palette" class="wayfinding-palette" role="listbox" [attr.aria-label]="t('wayfinding.palette.hint')">
                @if (paletteResult(); as found) {
                  @if (found.here) {
                    <section class="palette-section">
                      <p class="palette-kicker">{{ t('wayfinding.here.title') }}</p>
                      <a class="palette-row current" [routerLink]="found.here.route" (click)="closePalette()">
                        <i class="pi pi-map-marker" aria-hidden="true"></i>
                        <span>{{ found.here.label }}</span>
                      </a>
                    </section>
                  }
                  @if (!headerQuery().trim() && recents.recents().length > 0) {
                    <section class="palette-section">
                      <p class="palette-kicker">{{ t('wayfinding.recents.title') }}</p>
                      @for (recent of recents.recents(); track recent.route) {
                        <a class="palette-row" [routerLink]="recent.route" (click)="closePalette()">
                          <i class="pi pi-history" aria-hidden="true"></i>
                          <span>{{ recentLabel(recent) }}</span>
                        </a>
                      }
                    </section>
                  }
                  @if (found.workflows.length > 0) {
                    <section class="palette-section">
                      <p class="palette-kicker">{{ t('wayfinding.workflowMatch.title') }}</p>
                      @for (workflow of found.workflows; track workflow.id) {
                        <a class="palette-row" [routerLink]="workflow.route" (click)="closePalette()">
                          <i class="pi pi-sitemap" aria-hidden="true"></i>
                          <span>
                            <strong>{{ workflow.name }}</strong>
                            <small>{{ t('wayfinding.workflowMatch.description') }}</small>
                          </span>
                        </a>
                      }
                    </section>
                  }
                  @if (found.plays.length > 0) {
                    <section class="palette-section">
                      <p class="palette-kicker">{{ t(found.continued ? 'wayfinding.here.next' : 'wayfinding.results.title') }}</p>
                      @for (play of found.plays; track play.id) {
                        <button type="button" class="palette-row" (click)="openPlayFromPalette(play)">
                          <i class="pi pi-directions" aria-hidden="true"></i>
                          <span>
                            <strong>{{ t(play.titleKey) }}</strong>
                            <small>{{ play.steps[0] ? t(play.steps[0].titleKey) : t(play.descriptionKey) }}</small>
                          </span>
                        </button>
                      }
                    </section>
                  }
                  @if (found.jobs.length > 0) {
                    <section class="palette-section">
                      @for (job of found.jobs.slice(0, 4); track job.id) {
                        <a class="palette-row" [routerLink]="job.route" (click)="closePalette()">
                          <i class="pi pi-arrow-circle-right" aria-hidden="true"></i>
                          <span>{{ t(job.titleKey) }}</span>
                        </a>
                      }
                    </section>
                  }
                  @if (found.screens.length > 0) {
                    <section class="palette-section">
                      <p class="palette-kicker">{{ t('wayfinding.relatedScreens') }}</p>
                      @for (screen of found.screens; track screen.route) {
                        <a class="palette-row" [routerLink]="screen.route" (click)="closePalette()">
                          <i class="pi pi-window-maximize" aria-hidden="true"></i>
                          <span>{{ screen.label }}</span>
                        </a>
                      }
                    </section>
                  }
                  @if (headerQuery().trim() && found.plays.length === 0 && found.jobs.length === 0 && found.screens.length === 0 && found.workflows.length === 0) {
                    <p class="palette-empty">{{ t('wayfinding.results.empty') }}</p>
                  }
                }
              </div>
            }
          </form>
          <div class="user-menu">
            <select [value]="culture()" (change)="changeCulture($any($event.target).value)" aria-label="Language">
              <option value="fa-IR">فارسی</option>
              <option value="en-US">English</option>
            </select>
            <small>{{ context()?.subscriptionStatus ?? t('shell.noSubscription') }}</small>
            <div class="session-menu" (click)="$event.stopPropagation()">
              <button
                pButton
                type="button"
                severity="secondary"
                size="small"
                icon="pi pi-cog"
                [attr.aria-label]="t('shell.sessionMenu')"
                [attr.aria-expanded]="sessionMenuOpen()"
                aria-haspopup="menu"
                [class.session-menu-trigger--demo]="demoMode.enabled()"
                (click)="toggleSessionMenu()">
              </button>
              @if (sessionMenuOpen()) {
                <div class="session-menu-panel" role="menu">
                  <button
                    type="button"
                    class="tab-context-menu-item"
                    role="menuitemcheckbox"
                    [attr.aria-checked]="demoMode.enabled()"
                    (click)="toggleDemoMode()">
                    <span class="session-menu-check" aria-hidden="true">{{ demoMode.enabled() ? '✓' : '' }}</span>
                    <span>{{ t('shell.demoMode') }}</span>
                  </button>
                  <button type="button" class="tab-context-menu-item danger" role="menuitem" (click)="logout()">
                    {{ t('shell.logout') }}
                  </button>
                </div>
              }
            </div>
          </div>
        </header>
        @if (tabsService.hasTabs()) {
          <section class="tabs" aria-label="Open pages">
            @for (tab of tabsService.tabs(); track tab.key) {
              <button
                type="button"
                class="tab"
                [class.active]="tab.key === tabsService.activeTabKey()"
                (click)="tabsService.activateTab(tab.key)"
                (contextmenu)="openTabContextMenu($event, tab.key)">
                <span class="tab-title">{{ tab.title }}</span>
                <span
                  class="tab-close"
                  role="button"
                  tabindex="0"
                  aria-label="Close tab"
                  (click)="closeTab(tab.key, $event)"
                  (keydown.enter)="closeTab(tab.key, $event)"
                  (keydown.space)="closeTab(tab.key, $event)">
                  ×
                </span>
              </button>
            }
          </section>
        }
        @if (tabContextMenu().visible) {
          <div
            class="tab-context-menu"
            [style.left.px]="tabContextMenu().x"
            [style.top.px]="tabContextMenu().y"
            (click)="$event.stopPropagation()">
            <button type="button" class="tab-context-menu-item" (click)="closeOthersFromContextMenu()">
              {{ t('shell.tabs.closeOthers') }}
            </button>
            <button type="button" class="tab-context-menu-item danger" (click)="closeAllFromContextMenu()">
              {{ t('shell.tabs.closeAll') }}
            </button>
          </div>
        }
        <router-outlet />
      </main>
    </div>
  `,
  styles: [`
    .shell { display: flex; flex-direction: row-reverse; min-height: 100vh; }
    .shell.rtl { flex-direction: row; }
    aside { background: linear-gradient(180deg, var(--ain-color-primary), #1e3a8a); color: white; padding: 20px; border-inline-end: 1px solid rgb(255 255 255 / 14%); }
    aside { width: 300px; flex: 0 0 300px; }
    .context { font-size: 12px; opacity: .8; word-break: break-all; margin-top: 8px; }
    .switcher { display: grid; gap: 6px; margin-top: 12px; font-size: 12px; }
    .switcher select { border: 0; border-radius: 8px; padding: 8px; color: #0f172a; }
    nav { display: grid; gap: 8px; margin-top: 20px; max-height: calc(100vh - 130px); overflow: auto; padding-inline-end: 4px; }
    .nav-suite { display: grid; gap: 6px; }
    .nav-module { display: grid; gap: 4px; padding-inline-start: 8px; }
    .nav-capability { display: grid; gap: 4px; padding-inline-start: 12px; }
    .nav-toggle { display: flex; justify-content: space-between; align-items: center; gap: 6px; width: 100%; border: 0; border-radius: 8px; padding: 8px 10px; color: white; background: rgb(255 255 255 / 12%); font-weight: 600; cursor: pointer; text-align: start; font-size: 13px; }
    .nav-toggle.suite { background: rgb(255 255 255 / 18%); font-size: 14px; }
    .nav-toggle.module { font-size: 13px; }
    .nav-toggle.capability { font-size: 12px; font-weight: 500; background: rgb(255 255 255 / 8%); }
    .nav-toggle--platform-admin { background: rgb(251 191 36 / 22%); border-inline-start: 3px solid #fbbf24; }
    .nav-capability--platform-admin { border-top: 1px solid rgb(251 191 36 / 35%); padding-top: 4px; margin-top: 2px; }
    .nav-toggle-label { display: inline-flex; align-items: center; gap: 6px; min-width: 0; }
    .nav-platform-icon { flex: 0 0 auto; font-size: 11px; opacity: .9; line-height: 1; }
    .nav-leaves { display: grid; gap: 4px; padding-inline-start: 12px; }
    .nav-leaves.direct { padding-inline-start: 8px; }
    .nav-leaf { display: flex; justify-content: flex-start; align-items: center; gap: 6px; padding: 7px 10px; border-radius: 8px; text-decoration: none; color: white; background: rgb(255 255 255 / 8%); transition: background .2s ease; font-size: 12px; }
    .nav-leaf--platform-admin { background: rgb(251 191 36 / 14%); border-inline-start: 2px solid rgb(251 191 36 / 55%); }
    .nav-leaf:hover { background: rgb(255 255 255 / 16%); }
    .nav-leaf.active-link { background: rgb(255 255 255 / 24%); font-weight: 700; }
    main { flex: 1 1 auto; padding: 24px; min-width: 0; }
    header { display: flex; justify-content: space-between; gap: 12px; align-items: center; margin-bottom: 16px; }
    .nav-search { position: relative; flex: 1 1 240px; max-width: 420px; }
    .nav-search input { width: 100%; border: 1px solid #cbd5e1; border-radius: 8px; padding: 8px 10px; }
    .wayfinding-palette {
      position: absolute;
      top: calc(100% + 6px);
      inset-inline: 0;
      z-index: 1300;
      min-width: 280px;
      max-height: min(70vh, 420px);
      overflow: auto;
      border: 1px solid #c7d2fe;
      border-radius: 12px;
      background: white;
      box-shadow: 0 12px 28px rgb(15 23 42 / 16%);
      padding: 8px;
      display: grid;
      gap: 8px;
    }
    .palette-section { display: grid; gap: 4px; }
    .palette-kicker { margin: 0; padding: 4px 8px; font-size: 11px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: .02em; }
    .palette-row {
      display: flex;
      align-items: center;
      gap: 8px;
      width: 100%;
      border: 0;
      border-radius: 8px;
      padding: 8px 10px;
      background: transparent;
      color: #0f172a;
      text-align: start;
      text-decoration: none;
      cursor: pointer;
    }
    .palette-row small { display: block; color: #64748b; font-weight: 500; }
    .palette-row:hover, .palette-row.current { background: #eef2ff; }
    .palette-empty { margin: 0; padding: 8px 10px; color: #64748b; }
    .user-menu { display: flex; gap: 12px; align-items: center; }
    .session-menu { position: relative; }
    .session-menu-trigger--demo { box-shadow: 0 0 0 2px #fbbf24; }
    .session-menu-panel {
      position: absolute;
      inset-inline-end: 0;
      top: calc(100% + 6px);
      z-index: 1200;
      min-width: 180px;
      border: 1px solid #cbd5e1;
      border-radius: 10px;
      background: white;
      box-shadow: 0 10px 22px rgb(15 23 42 / 18%);
      padding: 6px;
      display: grid;
      gap: 4px;
    }
    .session-menu-check { display: inline-block; width: 1em; }
    .tabs { display: flex; gap: 4px; align-items: center; overflow-x: auto; padding-bottom: 10px; margin-bottom: 16px; border-bottom: 1px solid #e2e8f0; }
    .tab { position: relative; display: inline-flex; align-items: center; justify-content: center; border: 0; border-bottom-width: 2px; border-radius: 0; background: transparent; color: #0f172a; padding: 8px 28px; cursor: pointer; min-width: 140px; max-width: 260px; }
    .tab:hover { background: transparent; border-color: #94a3b8; }
    .tab.active { border-color: #3b82f6; border-bottom: 2px solid; border-bottom-color: #1d4ed8; font-weight: 700; }
    .tab-title { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; text-align: center; width: 100%; }
    .tab-close { position: absolute; inset-inline-end: 6px; border-radius: 999px; width: 18px; height: 18px; display: inline-flex; align-items: center; justify-content: center; font-size: 14px; line-height: 1; flex-shrink: 0; }
    .tab-close:hover { background: rgb(15 23 42 / 12%); }
    .tab-context-menu {
      position: fixed;
      z-index: 1200;
      min-width: 160px;
      border: 1px solid #cbd5e1;
      border-radius: 10px;
      background: white;
      box-shadow: 0 10px 22px rgb(15 23 42 / 18%);
      padding: 6px;
      display: grid;
      gap: 4px;
    }
    .tab-context-menu-item {
      text-align: start;
      border: 0;
      border-radius: 8px;
      padding: 8px 10px;
      background: transparent;
      color: #0f172a;
      cursor: pointer;
    }
    .tab-context-menu-item:hover { background: #eef2ff; }
    .tab-context-menu-item.danger { color: #991b1b; }
    .tab-context-menu-item.danger:hover { background: #fee2e2; }
  `]
})
export class ShellComponent implements OnInit {
  private readonly contextService = inject(CurrentContextService);
  private readonly themeService = inject(ThemeService);
  private readonly localizationService = inject(LocalizationService);
  private readonly tokenStorage = inject(TokenStorageService);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  readonly tabsService = inject(TabManagerService);
  private readonly tabTitleService = inject(TabTitleService);
  private readonly navigationTreeService = inject(NavigationTreeService);
  readonly demoMode = inject(DemoModeService);
  readonly recents = inject(WayfindingRecentsService);
  private readonly wayfinding = inject(WayfindingService);
  readonly sessionMenuOpen = signal(false);
  readonly headerQuery = signal('');
  readonly paletteOpen = signal(false);
  readonly currentUrl = signal('');

  readonly context = this.contextService.context;
  readonly appScope = signal(currentAppScope());
  readonly culture = this.localizationService.activeCulture;
  readonly direction = computed(() => this.context()?.direction ?? 'ltr');
  private readonly expandedNodes = signal<Set<string>>(new Set());
  readonly tabContextMenu = signal<{ visible: boolean; x: number; y: number; tabKey: string | null }>({
    visible: false,
    x: 0,
    y: 0,
    tabKey: null
  });
  readonly visibleNavigationItems = computed(() => this.navigationTreeService.visibleFlatItems());
  readonly navigationTree = computed(() => this.navigationTreeService.visibleNavigationTree());
  readonly contextRoute = computed(() => {
    const url = this.currentUrl() || this.router.url;
    const path = normalizeRoute(url);
    if (isWorkScreen(path)) {
      return path;
    }

    return this.router.parseUrl(url).queryParams['from'] ?? '';
  });
  readonly paletteResult = computed(() => {
    if (!this.paletteOpen()) {
      return null;
    }

    return this.wayfinding.search(this.headerQuery(), {
      currentRoute: this.contextRoute(),
      recents: this.recents.recents()
    });
  });

  constructor() {
    effect(() => {
      this.visibleNavigationItems();
      this.culture();
      this.tabTitleService.version();
      this.refreshOpenTabTitles();
    });
    const onKeydown = (event: KeyboardEvent) => this.onGlobalShortcut(event);
    window.addEventListener('keydown', onKeydown, true);
    inject(DestroyRef).onDestroy(() => window.removeEventListener('keydown', onKeydown, true));
  }

  ngOnInit(): void {
    this.contextService.loadContext().subscribe();
    this.contextService.loadNavigation().subscribe({
      next: () => {
        this.navigationTreeService.refreshMismatchReport();
        this.expandNodesForCurrentUrl();
      }
    });
    this.themeService.loadTheme().subscribe();
    this.localizationService.loadResources().subscribe();
    this.wayfinding.ensureDefinitionsLoaded().subscribe();
    this.restoreLastActiveTabIfNeeded();
    this.syncTabsWithCurrentRoute();
    this.currentUrl.set(this.router.url);
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(event => {
        this.currentUrl.set(event.urlAfterRedirects);
        this.hideOverlays();
        this.syncTabsWithCurrentRoute();
        this.expandNodesForCurrentUrl();
      });
  }

  t(key: string): string {
    return this.localizationService.translate(key);
  }

  translateOrFallback(key: string, fallback: string): string {
    const translated = this.t(key);
    return translated === key ? fallback : translated;
  }

  logout(): void {
    this.hideOverlays();
    this.tabsService.clear();
    this.tokenStorage.clear();
    void this.router.navigateByUrl('/login');
  }

  toggleSessionMenu(): void {
    this.hideTabContextMenu();
    this.sessionMenuOpen.update(open => !open);
  }

  toggleDemoMode(): void {
    this.demoMode.toggle();
  }

  submitHeaderSearch(event: Event): void {
    event.preventDefault();
    const q = this.headerQuery().trim();
    const from = this.contextRoute();
    this.closePalette();
    void this.router.navigate(['/dashboard'], { queryParams: { ...(q ? { q } : {}), ...(from ? { from } : {}) } });
  }

  onHeaderQuery(event: Event): void {
    this.headerQuery.set((event.target as HTMLInputElement).value ?? '');
    this.openPalette();
  }

  openPalette(): void {
    this.hideTabContextMenu();
    this.sessionMenuOpen.set(false);
    this.paletteOpen.set(true);
    this.wayfinding.ensureDefinitionsLoaded().subscribe();
  }

  closePalette(): void {
    this.paletteOpen.set(false);
  }

  openPlayFromPalette(play: WayfindingPlay): void {
    const typed = this.headerQuery().trim();
    const q = typed || this.t(play.titleKey);
    const from = this.contextRoute();
    this.closePalette();
    void this.router.navigate(['/dashboard'], {
      queryParams: { q, ...(from ? { from } : {}) }
    });
  }

  recentLabel(recent: WayfindingRecent): string {
    if (recent.labelKey) {
      const translated = this.t(recent.labelKey);
      if (translated && translated !== recent.labelKey) {
        return translated;
      }
    }

    return recent.fallbackLabel || recent.route;
  }

  onGlobalShortcut(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.paletteOpen()) {
      event.preventDefault();
      this.closePalette();
      return;
    }

    const isSearchChord = (event.ctrlKey || event.metaKey)
      && !event.altKey
      && (event.code === 'KeyK' || event.key.toLowerCase() === 'k' || event.key === 'ک');
    if (!isSearchChord) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    this.openPalette();
    const ask = document.getElementById('wayfinding-ask-header');
    if (ask instanceof HTMLInputElement) {
      ask.focus();
      ask.select();
    }
  }

  hideOverlays(): void {
    this.hideTabContextMenu();
    this.sessionMenuOpen.set(false);
    this.closePalette();
  }

  changeCulture(culture: string): void {
    this.localizationService.switchCulture(culture);
  }

  toggleNode(nodeKey: string): void {
    this.expandedNodes.update(current => {
      const next = new Set(current);
      if (next.has(nodeKey)) {
        next.delete(nodeKey);
      } else {
        next.add(nodeKey);
      }
      return next;
    });
  }

  isExpanded(nodeKey: string): boolean {
    return this.expandedNodes().has(nodeKey);
  }

  isPlatformAdminCapability(capabilityKey: string): boolean {
    return capabilityKey === 'ncx-platform-admin';
  }

  moduleNodeKey(suiteKey: string, moduleKey: string): string {
    return `${suiteKey}::${moduleKey}`;
  }

  capabilityNodeKey(suiteKey: string, moduleKey: string, capabilityKey: string): string {
    return `${suiteKey}::${moduleKey}::${capabilityKey}`;
  }

  private expandNodesForCurrentUrl(): void {
    const currentUrl = this.normalizeUrl(this.router.url);
    const tree = this.navigationTree();
    const toExpand = new Set<string>();

    for (const suite of tree) {
      for (const module of suite.modules) {
        const moduleKey = this.moduleNodeKey(suite.key, module.key);
        let moduleMatch = false;

        for (const cap of module.capabilities) {
          const capKey = this.capabilityNodeKey(suite.key, module.key, cap.key);
          if (cap.children.some(leaf => this.isNavigationRouteMatch(leaf.route, currentUrl))) {
            toExpand.add(suite.key);
            toExpand.add(moduleKey);
            toExpand.add(capKey);
            moduleMatch = true;
          }
        }

        if (module.directLeaves.some(leaf => this.isNavigationRouteMatch(leaf.route, currentUrl))) {
          toExpand.add(suite.key);
          toExpand.add(moduleKey);
          moduleMatch = true;
        }

        if (moduleMatch) {
          break;
        }
      }
    }

    if (toExpand.size > 0) {
      this.expandedNodes.set(toExpand);
    } else if (tree[0]) {
      this.expandedNodes.set(new Set([tree[0].key]));
    }
  }

  isOrganizationScope(): boolean {
    return this.appScope() === 'organization';
  }

  closeTab(tabKey: string, event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    this.hideOverlays();
    this.tabsService.closeTab(tabKey);
  }

  openTabContextMenu(event: MouseEvent, tabKey: string): void {
    event.preventDefault();
    event.stopPropagation();
    this.sessionMenuOpen.set(false);
    this.tabContextMenu.set({
      visible: true,
      x: event.clientX,
      y: event.clientY,
      tabKey
    });
  }

  closeOthersFromContextMenu(): void {
    const selectedTabKey = this.tabContextMenu().tabKey;
    if (!selectedTabKey) {
      return;
    }

    this.tabsService.closeOtherTabs(selectedTabKey);
    this.hideOverlays();
  }

  closeAllFromContextMenu(): void {
    this.tabsService.closeAllTabs();
    this.hideOverlays();
  }

  hideTabContextMenu(): void {
    if (!this.tabContextMenu().visible) {
      return;
    }

    this.tabContextMenu.set({
      visible: false,
      x: 0,
      y: 0,
      tabKey: null
    });
  }

  private getModuleTitleKey(moduleKey: string): string {
    const keyByModule: Record<string, string> = {
      general: 'shell.module.general',
      'platform-core': 'shell.module.platformCore',
      'party-hub-customers': 'nav.module.partyHubCustomers',
      'party-hub-parties': 'nav.module.partyHubParties',
      'party-hub-identity': 'nav.module.partyHubIdentity',
      'identity-access': 'nav.module.partyHubIdentity',
      'subscription-billing': 'shell.module.subscriptionBilling',
      catalog: 'shell.module.catalog',
      'universal-sales': 'shell.module.universalSales',
      'workflow-bpms': 'shell.module.workflowBpms',
      'dynamic-forms-entities': 'shell.module.dynamicFormsEntities',
      'financial-core-ufc': 'shell.module.financialCore',
      'payments-wallet-lending': 'shell.module.payments',
      'legacy-sales-commerce': 'shell.module.legacySalesCommerce',
      'legacy-sales-reports': 'shell.module.legacySalesReports',
      'legacy-payments-cashflow': 'shell.module.legacyPaymentsCashflow',
      'legacy-payments-reports': 'shell.module.legacyPaymentsReports',
      'legacy-accounting-management': 'shell.module.legacyAccountingManagement',
      'legacy-lending-suite': 'shell.module.legacyLendingSuite',
      'legacy-transaction-settlement': 'shell.module.legacyTransactionSettlement',
      'legacy-assets-management': 'shell.module.legacyAssetsManagement'
    };
    return keyByModule[moduleKey] ?? 'shell.module.general';
  }

  private humanizeModuleKey(moduleKey: string): string {
    return moduleKey
      .split(/[-_.]/g)
      .filter(Boolean)
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  private syncTabsWithCurrentRoute(): void {
    const currentUrl = this.normalizeUrl(this.router.url);
    if (!currentUrl || currentUrl === '/login') {
      return;
    }

    this.tabsService.openOrActivate(currentUrl, this.resolveCurrentRouteTitle(currentUrl));
  }

  private restoreLastActiveTabIfNeeded(): void {
    const currentUrl = this.normalizeUrl(this.router.url);
    const activeTab = this.tabsService.activeTab();
    if (!activeTab) {
      return;
    }

    if (currentUrl === '/dashboard' && activeTab.url !== '/dashboard') {
      void this.router.navigateByUrl(activeTab.url);
    }
  }

  private resolveCurrentRouteTitle(currentUrl: string): string {
    const normalizedCurrentUrl = this.normalizeUrl(currentUrl);
    const overrideTitle = this.tabTitleService.getForUrl(normalizedCurrentUrl);
    if (overrideTitle) {
      return overrideTitle;
    }

    const routeTitleFromConfig = this.resolveRouteConfiguredTitle(normalizedCurrentUrl);
    if (routeTitleFromConfig) {
      return routeTitleFromConfig;
    }

    const matchedNavItem = this.visibleNavigationItems()
      .find(item => this.isNavigationRouteMatch(this.normalizeUrl(item.route), normalizedCurrentUrl));
    if (matchedNavItem) {
      return this.t(matchedNavItem.label);
    }

    const leaf = this.getLeafRouteSnapshot();
    return this.resolveFallbackTitle(normalizedCurrentUrl, leaf?.routeConfig?.path ?? '');
  }

  private getLeafRouteSnapshot() {
    let route = this.activatedRoute.snapshot;
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }

  private normalizeUrl(url: string): string {
    const path = url.split(/[?#]/)[0] ?? url;
    return path.startsWith('/') ? path : `/${path}`;
  }

  private refreshOpenTabTitles(): void {
    for (const tab of this.tabsService.tabs()) {
      const resolvedTitle = this.resolveCurrentRouteTitle(tab.url);
      this.tabsService.updateTabTitle(tab.url, resolvedTitle);
    }
  }

  private resolveRouteConfiguredTitle(currentUrl: string): string | null {
    const route = this.findMatchingRoute(currentUrl);
    if (!route?.data) {
      return null;
    }

    const tabTitle = route.data['tabTitle'];
    if (typeof tabTitle === 'string' && tabTitle.trim().length > 0) {
      return tabTitle.trim();
    }

    const tabTitleKey = route.data['tabTitleKey'];
    if (typeof tabTitleKey === 'string' && tabTitleKey.length > 0) {
      return this.t(tabTitleKey);
    }

    const titleKey = route.data['titleKey'];
    if (typeof titleKey === 'string' && titleKey.length > 0) {
      return this.t(titleKey);
    }

    return null;
  }

  private findMatchingRoute(url: string): Route | null {
    const normalizedUrl = this.normalizeUrl(url);
    const targetSegments = normalizedUrl.split('/').filter(Boolean);
    const stack: Route[] = this.router.config.flatMap(route => route.children ?? [route]);

    for (const route of stack) {
      const path = (route.path ?? '').trim();
      if (!path || path === '**') {
        continue;
      }

      if (this.doesRoutePathMatch(path, targetSegments)) {
        return route;
      }
    }

    return null;
  }

  private doesRoutePathMatch(routePath: string, targetSegments: string[]): boolean {
    const routeSegments = routePath.split('/').filter(Boolean);
    if (routeSegments.length !== targetSegments.length) {
      return false;
    }

    for (let index = 0; index < routeSegments.length; index += 1) {
      const routeSegment = routeSegments[index];
      const targetSegment = targetSegments[index];
      if (routeSegment.startsWith(':')) {
        continue;
      }

      if (routeSegment !== targetSegment) {
        return false;
      }
    }

    return true;
  }

  private isNavigationRouteMatch(navRoute: string, currentUrl: string): boolean {
    if (!navRoute) {
      return false;
    }

    return currentUrl === navRoute || currentUrl.startsWith(`${navRoute}/`);
  }

  private resolveFallbackTitle(currentUrl: string, routePath: string): string {
    const routeSegments = routePath
      .split('/')
      .filter(segment => Boolean(segment) && !segment.startsWith(':'));
    const pathSegments = currentUrl.split('/').filter(Boolean);
    const candidate = routeSegments.at(-1)
      ?? this.pickReadableSegment(pathSegments)
      ?? 'page';
    return this.humanizeModuleKey(candidate.replaceAll(':', ''));
  }

  private pickReadableSegment(pathSegments: string[]): string | null {
    for (let index = pathSegments.length - 1; index >= 0; index -= 1) {
      const segment = pathSegments[index];
      if (!segment) {
        continue;
      }

      const looksLikeId = /^\d+$/.test(segment) || /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(segment);
      if (!looksLikeId) {
        return segment;
      }
    }

    return null;
  }
}
